/**
 * 首页布局：服务端 /api/user/dashboard/layout 是事实源（含产品卡片自动加入、移除记忆 seen）；
 * 本地只做缓存 / 表不可用时的兜底。
 */

export const SIZES = ['third', 'half', 'full'];
export const SIZE_LABEL = { third: '1/3', half: '1/2', full: '整行' };

export const WIDGET_TITLES = {
    onboarding: 'Getting started',
    attention: '需要关注',
    account: '账户概览',
    services: '我的服务',
    cdn: 'CDN 统计',
    dns: 'DNS 统计',
    evm: '云服务器',
    host_shared: '共享主机',
    container: '独享容器',
    vpc: '网络',
    domain: '域名',
    ssl: 'SSL 证书',
    mail: '邮箱',
    tm: '邮件推送',
    backup: '备份',
    activity: '最近账单 / 活动',
    quick: '快速入口',
};

/** Views per widget (first = default). Mirrors DashboardLayoutService::WIDGETS. */
export const VIEWS = {
    account: [['overview', '概览'], ['renewals', '续费'], ['invoices', '账单']],
    cdn: [['traffic', '流量'], ['requests', '请求'], ['top', '域名排行'], ['hit', '命中率']],
    dns: [['queries', '查询'], ['zones', '区域'], ['top', '区域排行']],
    evm: [['overview', '概览'], ['list', '实例'], ['usage', '用量'], ['expiring', '到期']],
    host_shared: [['sites', '站点'], ['usage', '用量'], ['expiring', '到期']],
    container: [['list', '容器'], ['usage', '用量'], ['pending', '开通中']],
    vpc: [['vpc', 'VPC'], ['eip', '弹性 IP']],
    domain: [['list', '域名'], ['expiring', '到期'], ['autorenew', '自动续费']],
    ssl: [['list', '证书'], ['expiring', '到期'], ['status', '签发状态']],
    mail: [['usage', '用量'], ['domains', '域名']],
    tm: [['stats', '发送统计'], ['streams', '发信流']],
    backup: [['resources', '资源'], ['recent', '最近备份']],
};

export const DEFAULT_SIZE = {
    onboarding: 'full', attention: 'half', account: 'half', services: 'full', cdn: 'half', dns: 'half', evm: 'full', host_shared: 'half',
    container: 'half', vpc: 'third', domain: 'third', ssl: 'third', mail: 'third', tm: 'third', backup: 'third',
    activity: 'half', quick: 'half',
};

export const ALL_KEYS = Object.keys(DEFAULT_SIZE);

export const viewOf = (w) => {
    const vs = VIEWS[w.key];
    if (!vs) return null;
    return vs.some(([v]) => v === w.view) ? w.view : vs[0][0];
};

/** Fallback when the server layout can't be loaded: core cards only (product cards come from the server). */
const FALLBACK = ['onboarding', 'attention', 'account', 'services', 'activity', 'quick'];

/** Any saved/served layout → valid layout. v1 `visible:false` = removed. */
export const mergeLayout = (saved) => {
    const src = saved && Array.isArray(saved.widgets) && saved.widgets.length ? saved.widgets : FALLBACK.map((key) => ({ key }));
    const seen = new Set();
    const out = [];
    src.forEach((w) => {
        if (!w || !DEFAULT_SIZE[w.key] || seen.has(w.key)) return;
        seen.add(w.key);
        if (w.visible === false) return;
        const e = { key: w.key, size: SIZES.includes(w.size) ? w.size : DEFAULT_SIZE[w.key] };
        const v = viewOf(w);
        if (v) e.view = v;
        out.push(e);
    });
    return { version: 2, widgets: out };
};

const lsKey = (uid) => `yc.dashboard.layout.v2.${uid || 'anon'}`;

export const readLocal = (uid) => {
    try {
        const raw = window.localStorage.getItem(lsKey(uid));
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

export const writeLocal = (uid, layout) => {
    try {
        if (layout) window.localStorage.setItem(lsKey(uid), JSON.stringify(layout));
        else window.localStorage.removeItem(lsKey(uid));
    } catch (e) {
        /* private mode / blocked storage: layout just isn't remembered */
    }
};
